import { Mail } from "lucide-react";
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router";

import { ApiError, createPlatformOrg } from "@/api/client";
import { Field, SELECT, aYearOn, isoDay } from "@/screens/platform/fields";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Card, Spinner } from "@/ui/primitives";

/** "Northern Glazing Ltd." becomes "northern-glazing-ltd". */
export function slugOf(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/**
 * A new customer organization, for Darkstar staff (Phase 6 Task 8, ADR-0015): its name,
 * its licence and its first owner, made in one step. The owner is mailed an invite.
 */
export function NewOrganizationScreen() {
  const navigate = useNavigate();
  const today = new Date();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState<"trial" | "active">("trial");
  const [seats, setSeats] = useState("3");
  const [starts, setStarts] = useState(isoDay(today));
  const [ends, setEnds] = useState(aYearOn(today));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await createPlatformOrg({
        name: name.trim(),
        slug,
        owner_email: owner.trim(),
        licence: { seats: Number(seats), starts, ends, status },
      });
      void navigate(`/platform/orgs/${created.organization.id}`, {
        state: { invited: owner.trim() },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not reach the server.");
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8 lg:px-8">
      <PageHeader
        title="New organization"
        description="The organization, its licence and its first owner. The owner is mailed an invite to choose a password; they invite everyone else."
      />
      <form className="mt-6 space-y-6" onSubmit={(event) => void submit(event)}>
        {error ? <Notice tone="danger">{error}</Notice> : null}

        <Card className="space-y-4 p-5">
          <h2 className="text-sm font-semibold text-ink">Organization</h2>
          <Field label="Name">
            <Input
              required
              autoFocus
              maxLength={200}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (!slugTouched) setSlug(slugOf(event.target.value));
              }}
            />
          </Field>
          <Field label="Slug" hint="Lowercase letters, digits and hyphens. It cannot be changed.">
            <Input
              required
              minLength={2}
              maxLength={64}
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              value={slug}
              onChange={(event) => {
                setSlugTouched(true);
                setSlug(event.target.value);
              }}
            />
          </Field>
          <Field label="First owner's email">
            <Input
              type="email"
              required
              maxLength={320}
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
            />
          </Field>
        </Card>

        <Card className="space-y-4 p-5">
          <h2 className="text-sm font-semibold text-ink">Licence</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Licence">
              <select
                className={SELECT}
                value={status}
                onChange={(event) => setStatus(event.target.value as "trial" | "active")}
              >
                <option value="trial">Trial</option>
                <option value="active">Active</option>
              </select>
            </Field>
            <Field label="Seats" hint="Members plus pending invites.">
              <Input
                type="number"
                required
                min={1}
                max={10000}
                value={seats}
                onChange={(event) => setSeats(event.target.value)}
              />
            </Field>
            <Field label="Starts">
              <Input
                type="date"
                required
                value={starts}
                onChange={(event) => setStarts(event.target.value)}
              />
            </Field>
            <Field label="Ends" hint="Past this day the organization is read-only.">
              <Input
                type="date"
                required
                min={starts}
                value={ends}
                onChange={(event) => setEnds(event.target.value)}
              />
            </Field>
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button asChild>
            <Link to="/platform/orgs">Cancel</Link>
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? <Spinner className="size-4" /> : <Mail aria-hidden="true" />}
            Create and invite the owner
          </Button>
        </div>
      </form>
    </main>
  );
}
